import { useState, useMemo } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Play } from "lucide-react";
// iter87.180.9 — Swap window.confirm for BrutalConfirm (iOS Safari
// silently drops native confirms inside async handlers).
import { useBrutalConfirm } from "@/components/BrutalConfirm";

export default function MatchesTab({ tournament, data, isLocked, onChanged }) {
    const [matchStage, setMatchStage] = useState("group");
    const { confirm: brutalConfirm, hostElement: confirmHost } = useBrutalConfirm();
    const [matchGroup, setMatchGroup] = useState("");
    const [teamA, setTeamA] = useState("");
    const [teamB, setTeamB] = useState("");
    const [scoreA, setScoreA] = useState("");
    const [scoreB, setScoreB] = useState("");
    const [editingMatchId, setEditingMatchId] = useState(null);

    const matchStageOptions = useMemo(
        () => (tournament?.stages || []).filter(s => s.key !== "champion"),
        [tournament?.stages]
    );

    const handleSubmitMatch = async () => {
        if (!teamA || !teamB || scoreA === "" || scoreB === "" || teamA === teamB) {
            toast.error("Pick two different teams and enter scores");
            return;
        }
        try {
            const payload = {
                stage: matchStage,
                group: matchGroup || null,
                team_a_id: teamA,
                team_b_id: teamB,
                score_a: parseInt(scoreA),
                score_b: parseInt(scoreB),
            };
            if (editingMatchId) {
                await api.updateMatch(editingMatchId, payload);
                toast.success("Match corrected");
            } else {
                await api.submitMatch(tournament.id, payload);
                toast.success("Match recorded");
            }
            setEditingMatchId(null);
            setScoreA(""); setScoreB(""); setTeamA(""); setTeamB(""); setMatchGroup("");
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed");
        }
    };

    const handleEditMatch = (m) => {
        setEditingMatchId(m.id);
        setMatchStage(m.stage || "group");
        setMatchGroup(m.group || "");
        setTeamA(m.team_a_id || "");
        setTeamB(m.team_b_id || "");
        setScoreA(String(m.score_a ?? ""));
        setScoreB(String(m.score_b ?? ""));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleCancelEdit = () => {
        setEditingMatchId(null);
        setScoreA(""); setScoreB(""); setTeamA(""); setTeamB(""); setMatchGroup("");
    };

    const handleDeleteMatch = async (m) => {
        const ok = await brutalConfirm({
            title: "Delete this match?",
            body: `${m.stage} · ${m.score_a}-${m.score_b}\n\nGroup-stage stats will be reverted automatically.`,
            confirmLabel: "Delete",
            destructive: true,
        });
        if (!ok) return;
        try {
            await api.deleteMatch(m.id);
            toast.success("Match deleted");
            if (editingMatchId === m.id) handleCancelEdit();
            onChanged?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to delete");
        }
    };

    return (
        <div className="brutal-card p-5 space-y-4">
            <h2 className="font-display font-extrabold text-xl uppercase">Record a Match</h2>
            <div className="grid sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs uppercase font-bold text-zinc-500 mb-1">Stage</label>
                    <select
                        data-testid="match-stage-select"
                        value={matchStage} onChange={(e) => setMatchStage(e.target.value)}
                        className="brutal-input w-full">
                        {matchStageOptions.map(s => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-xs uppercase font-bold text-zinc-500 mb-1">Group (optional)</label>
                    <input
                        data-testid="match-group-input"
                        value={matchGroup} onChange={(e) => setMatchGroup(e.target.value)}
                        placeholder="A" className="brutal-input w-full" />
                </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
                <div className="col-span-2">
                    <label className="block text-xs uppercase font-bold text-zinc-500 mb-1">Team A</label>
                    <select data-testid="team-a-select" value={teamA} onChange={(e) => setTeamA(e.target.value)} className="brutal-input w-full">
                        <option value="">Select...</option>
                        {tournament.teams.map(t => <option key={t.id} value={t.id}>{t.flag || ""} {t.name}</option>)}
                    </select>
                </div>
                <div className="grid grid-cols-2 gap-2">
                    <input data-testid="score-a-input" type="number" min="0" value={scoreA} onChange={(e) => setScoreA(e.target.value)} placeholder="A" className="brutal-input w-full text-center font-mono font-bold text-lg" />
                    <input data-testid="score-b-input" type="number" min="0" value={scoreB} onChange={(e) => setScoreB(e.target.value)} placeholder="B" className="brutal-input w-full text-center font-mono font-bold text-lg" />
                </div>
                <div className="col-span-2">
                    <label className="block text-xs uppercase font-bold text-zinc-500 mb-1">Team B</label>
                    <select data-testid="team-b-select" value={teamB} onChange={(e) => setTeamB(e.target.value)} className="brutal-input w-full">
                        <option value="">Select...</option>
                        {tournament.teams.map(t => <option key={t.id} value={t.id}>{t.flag || ""} {t.name}</option>)}
                    </select>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <button data-testid="submit-match-btn" onClick={handleSubmitMatch} disabled={isLocked} className="brutal-btn-primary disabled:opacity-40">
                    <Play className="w-4 h-4" /> {editingMatchId ? "Save correction" : "Record Match"}
                </button>
                {editingMatchId && (
                    <button data-testid="cancel-edit-match-btn" onClick={handleCancelEdit} className="brutal-btn-ghost text-xs">
                        Cancel edit
                    </button>
                )}
            </div>

            {/* iter48 — Persistent match log. Was capped at 30 + flat list;
                now groups by stage with sticky headers, shows ALL matches,
                and surfaces a status badge per row. Still drives the same
                Edit / Delete actions as before. */}
            <MatchLog
                matches={data.matches || []}
                tournament={tournament}
                editingMatchId={editingMatchId}
                onEdit={handleEditMatch}
                onDelete={handleDeleteMatch}
            />
            {confirmHost}
        </div>
    );
}

const STAGE_LABEL = {
    group: "Group stage",
    r64: "Round of 64",
    r32: "Round of 32",
    r16: "Round of 16",
    qf: "Quarter-finals",
    sf: "Semi-finals",
    final: "Final",
};
const STAGE_ORDER = ["group", "r64", "r32", "r16", "qf", "sf", "final"];

function MatchLog({ matches, tournament, editingMatchId, onEdit, onDelete }) {
    // Group by stage, preserve insertion order within each stage
    const byStage = useMemo(() => {
        const out = {};
        for (const m of matches) {
            const k = m.stage || "group";
            if (!out[k]) out[k] = [];
            out[k].push(m);
        }
        // Most-recent first within each stage
        for (const k of Object.keys(out)) {
            out[k].sort((a, b) => (b.played_at || "").localeCompare(a.played_at || ""));
        }
        return out;
    }, [matches]);

    const stagesPresent = STAGE_ORDER.filter(s => byStage[s]?.length);
    // Surface any unknown stages (defensive) at the end
    Object.keys(byStage).forEach(k => {
        if (!STAGE_ORDER.includes(k)) stagesPresent.push(k);
    });

    if (matches.length === 0) {
        return (
            <div className="pt-4 border-t-2 border-[#333]">
                <p className="text-xs uppercase font-bold text-zinc-500 mb-2">Match log</p>
                <p className="text-zinc-600 text-sm">No matches recorded yet.</p>
            </div>
        );
    }

    return (
        <div className="pt-4 border-t-2 border-[#333]" data-testid="match-log">
            <div className="flex items-center justify-between mb-3">
                <p className="text-xs uppercase font-bold text-zinc-500">
                    Match log <span className="text-cyber font-mono">({matches.length})</span>
                </p>
                <p className="text-[10px] text-zinc-600 uppercase tracking-wider">
                    Grouped by stage · newest first
                </p>
            </div>
            <div className="space-y-4 max-h-[40rem] overflow-y-auto pr-1">
                {stagesPresent.map(stage => (
                    <div key={stage} data-testid={`match-log-stage-${stage}`}>
                        <div className="sticky top-0 z-10 -mx-1 px-1 py-1 bg-ink/95 backdrop-blur border-b border-[#222] flex items-center gap-2 mb-1.5">
                            <span className="pill bg-cyber/10 border-cyber/40 text-cyber text-[10px]">
                                {STAGE_LABEL[stage] || stage.toUpperCase()}
                            </span>
                            <span className="text-[10px] text-zinc-500 tabular-nums">
                                {byStage[stage].length} match{byStage[stage].length === 1 ? "" : "es"}
                            </span>
                        </div>
                        <div className="space-y-1">
                            {byStage[stage].map(m => {
                                const tA = tournament.teams.find(t => t.id === m.team_a_id);
                                const tB = tournament.teams.find(t => t.id === m.team_b_id);
                                const winnerName = m.is_draw ? "Draw" :
                                    (m.winner_id === tA?.id ? tA?.name : tB?.name);
                                const isEditingThis = editingMatchId === m.id;
                                return (
                                    <div
                                        key={m.id}
                                        data-testid={`match-log-row-${m.id}`}
                                        className={`flex items-center justify-between gap-2 p-2 border rounded text-xs bg-ink ${isEditingThis ? "border-cyber bg-cyber/5" : "border-[#222]"}`}
                                    >
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <span className="pill bg-neon/10 border-neon/40 text-neon text-[9px] shrink-0">
                                                Completed
                                            </span>
                                            <span className="truncate">
                                                {tA?.flag} {tA?.name}
                                                <span className="font-mono font-bold mx-1.5">{m.score_a}-{m.score_b}</span>
                                                {tB?.flag} {tB?.name}
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-1 shrink-0">
                                            <span className="text-[10px] text-zinc-500 mr-1 hidden md:inline">
                                                {m.espn_id ? "ESPN" : "Manual"} · {winnerName}
                                            </span>
                                            {m.played_at && (
                                                <span className="text-[10px] text-zinc-600 mr-1 hidden lg:inline tabular-nums">
                                                    {new Date(m.played_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })}
                                                </span>
                                            )}
                                            <button
                                                data-testid={`edit-match-${m.id}-btn`}
                                                onClick={() => onEdit(m)}
                                                className="brutal-btn-ghost text-[10px] px-2 py-1"
                                                title="Edit match"
                                            >
                                                Edit
                                            </button>
                                            <button
                                                data-testid={`delete-match-${m.id}-btn`}
                                                onClick={() => onDelete(m)}
                                                className="brutal-btn-ghost text-[10px] px-2 py-1 text-blaze hover:bg-blaze/20"
                                                title="Delete match"
                                            >
                                                Delete
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
